import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth'
import { doc, setDoc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore'
import { auth, db } from '../config/firebase'

/**
 * Register a new super admin (first-time setup)
 * Creates Firebase Auth user and Firestore user document
 */
export const registerSuperAdmin = async (email, password, fullName, employeeId) => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Update user profile with display name
    await updateProfile(user, {
      displayName: fullName
    })

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: email,
      fullName: fullName,
      employeeId: employeeId,
      role: 'super_admin',
      department: 'Administration',
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })

    return { success: true, user }
  } catch (error) {
    console.error('Registration error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Login with email and password
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid))

    if (userDoc.exists()) {
      return { success: true, user: userDoc.data() }
    } else {
      // User auth exists but no Firestore document
      return {
        success: false,
        error: 'User profile not found. Please contact administrator.'
      }
    }
  } catch (error) {
    console.error('Login error:', error)

    // User-friendly error messages
    let errorMessage = 'Login failed. Please try again.'

    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email.'
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password.'
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.'
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later.'
    }

    return { success: false, error: errorMessage }
  }
}

/**
 * Logout current user
 */
export const logout = async () => {
  try {
    await signOut(auth)
    return { success: true }
  } catch (error) {
    console.error('Logout error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get current authenticated user
 */
export const getCurrentUser = () => {
  return auth.currentUser
}

/**
 * Get user data from Firestore
 */
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid))
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() }
    } else {
      return { success: false, error: 'User not found' }
    }
  } catch (error) {
    console.error('Get user data error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Auth state observer
 * Returns unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User is signed in, get additional data from Firestore
      const userData = await getUserData(user.uid)
      callback(userData.success ? userData.data : null)
    } else {
      // User is signed out
      callback(null)
    }
  })
}

/**
 * Register a new employee (admin-only function)
 * Creates Firebase Auth user and Firestore user document with employee role
 */
export const registerEmployee = async (
  email,
  password,
  fullName,
  employeeId,
  department,
  reportsTo,
  reportsToName
) => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Update user profile with display name
    await updateProfile(user, {
      displayName: fullName
    })

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: email,
      fullName: fullName,
      employeeId: employeeId,
      role: 'employee',  // NOT super_admin
      department: department,
      reportsTo: reportsTo,  // Manager's employeeId
      reportsToName: reportsToName,  // Manager's fullName
      status: 'active',
      passwordChangeRequired: true,  // Force password change on first login
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })

    return { success: true, user }
  } catch (error) {
    console.error('Employee registration error:', error)

    // User-friendly error messages
    let errorMessage = 'Registration failed. Please try again.'

    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'Email already in use. Please use a different email.'
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.'
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password is too weak. Use at least 6 characters.'
    }

    return { success: false, error: errorMessage }
  }
}

/**
 * Get users by role (for dropdowns and filtering)
 * @param {string} role - Role to filter by ('super_admin', 'admin', 'employee')
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getUsersByRole = async (role) => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('role', '==', role),
      orderBy('fullName', 'asc')
    )

    const querySnapshot = await getDocs(usersQuery)
    const users = []

    querySnapshot.forEach((doc) => {
      users.push({
        uid: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: users }
  } catch (error) {
    console.error('Get users by role error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Send password reset email to user
 * @param {string} email - User's email address
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email)
    return { success: true }
  } catch (error) {
    console.error('Password reset error:', error)

    // User-friendly error messages
    let errorMessage = 'Failed to send password reset email. Please try again.'

    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email.'
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.'
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many requests. Please try again later.'
    }

    return { success: false, error: errorMessage }
  }
}

/**
 * Change user's password (requires current password for reauthentication)
 * @param {string} currentPassword - User's current password
 * @param {string} newPassword - New password to set
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser

    if (!user || !user.email) {
      return {
        success: false,
        error: 'No authenticated user found. Please log in again.'
      }
    }

    // Reauthenticate user before password change (Firebase security requirement)
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)

    // Update password
    await updatePassword(user, newPassword)

    return { success: true }
  } catch (error) {
    console.error('Change password error:', error)

    // User-friendly error messages
    let errorMessage = 'Failed to change password. Please try again.'

    if (error.code === 'auth/wrong-password') {
      errorMessage = 'Current password is incorrect.'
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'New password is too weak. Use at least 6 characters.'
    } else if (error.code === 'auth/requires-recent-login') {
      errorMessage = 'Please log out and log in again before changing your password.'
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many attempts. Please try again later.'
    }

    return { success: false, error: errorMessage }
  }
}
